/**
 * SVG Layer for edge rendering.
 *
 * Renders edges as SVG paths using CSS transforms for pan/zoom.
 * Sits underneath the HTML layer so edges are always behind nodes.
 *
 * Key architecture:
 * - Edges are positioned in WORLD coordinates
 * - A single CSS transform on the SVG handles camera pan/zoom
 * - Bezier curves for smooth edge routing
 *
 * Performance optimizations:
 * - Only updates edges when source/target nodes move
 * - Caches path data to avoid recalculation
 */

import type { Camera, UIEdge, UINode, ViewportSize, WorldPoint } from './types';

/** Cached state for an edge to detect changes */
interface EdgeState {
	sourceX: number;
	sourceY: number;
	targetX: number;
	targetY: number;
	selected: boolean;
	hovered: boolean;
	type: string;
}

/** CSS class names used by the SVG layer */
const CSS = {
	layer: 'canvas-svg-layer',
	edge: 'canvas-edge',
	selected: 'selected',
	hovered: 'hovered'
} as const;

/** SVG namespace */
const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Calculate control point offset for Bezier curve based on distance.
 * Creates a gentle curve that scales with distance.
 */
function getControlPointOffset(source: WorldPoint, target: WorldPoint): number {
	const dx = target.x - source.x;
	const dy = target.y - source.y;
	const distance = Math.sqrt(dx * dx + dy * dy);

	// Curve amount scales with distance, clamped to reasonable range
	return Math.min(Math.max(distance * 0.3, 30), 150);
}

/**
 * Generate SVG path data for an edge.
 * Uses a straight line from source to target center.
 */
function generateEdgePath(source: WorldPoint, target: WorldPoint): string {
	return `M ${source.x} ${source.y} L ${target.x} ${target.y}`;
}

/**
 * Calculate edge endpoint from node center.
 * Node position is top-left, so we add half-size to get center.
 */
function getNodeCenter(node: UINode): WorldPoint {
	return {
		x: node.position.x + node.size.width / 2,
		y: node.position.y + node.size.height / 2
	};
}

/**
 * SVG Layer manages SVG elements for canvas edges.
 *
 * Features:
 * - Single CSS transform for camera (GPU-accelerated)
 * - Edges positioned in world coordinates
 * - Bezier curve routing
 * - Visual state classes (selected, hovered)
 * - Dirty tracking to skip unchanged edges
 */
export class SVGLayer {
	private container: HTMLElement;
	private svg: SVGSVGElement;
	private edgeGroup: SVGGElement;
	private camera: Camera = { x: 0, y: 0, zoom: 1 };
	private viewport: ViewportSize = { width: 0, height: 0 };

	/** Map of edge ID to SVG path element */
	private edgeElements: Map<string, SVGPathElement> = new Map();

	/** Map of edge ID to cached state for change detection */
	private edgeStates: Map<string, EdgeState> = new Map();

	/** Current set of rendered edge IDs */
	private renderedEdgeIds: Set<string> = new Set();

	constructor(container: HTMLElement) {
		this.container = container;
		this.container.classList.add(CSS.layer);
		this.svg = this.createSVG();
		this.edgeGroup = this.createEdgeGroup();
	}

	private createSVG(): SVGSVGElement {
		const svg = document.createElementNS(SVG_NS, 'svg');
		svg.style.position = 'absolute';
		svg.style.top = '0';
		svg.style.left = '0';
		svg.style.width = '100%';
		svg.style.height = '100%';
		svg.style.overflow = 'visible';
		svg.style.pointerEvents = 'none'; // Edges don't capture mouse events
		this.container.appendChild(svg);
		return svg;
	}

	private createEdgeGroup(): SVGGElement {
		const group = document.createElementNS(SVG_NS, 'g');
		group.style.transformOrigin = '0 0';
		group.style.willChange = 'transform';
		this.svg.appendChild(group);
		return group;
	}

	/**
	 * Set the camera for world → screen transforms.
	 * Updates the CSS transform on the edge group.
	 */
	setCamera(camera: Camera): void {
		this.camera = camera;
		this.updateTransform();
	}

	/**
	 * Set viewport dimensions.
	 */
	setViewportSize(width: number, height: number): void {
		this.viewport = { width, height };
		this.updateTransform();
	}

	/**
	 * Update the CSS transform based on camera and viewport.
	 * Same transform as HTML layer for consistency.
	 */
	private updateTransform(): void {
		const { x, y, zoom } = this.camera;
		const { width, height } = this.viewport;

		// Transform: move to center, scale, then offset by camera
		this.edgeGroup.style.transform = `translate(${width / 2}px, ${height / 2}px) scale(${zoom}) translate(${-x}px, ${-y}px)`;
	}

	/**
	 * Check if an edge has changed compared to cached state.
	 */
	private edgeChanged(
		edge: UIEdge,
		sourceNode: UINode,
		targetNode: UINode,
		state: EdgeState
	): boolean {
		return (
			state.sourceX !== sourceNode.position.x ||
			state.sourceY !== sourceNode.position.y ||
			state.targetX !== targetNode.position.x ||
			state.targetY !== targetNode.position.y ||
			state.selected !== (edge.selected ?? false) ||
			state.hovered !== (edge.hovered ?? false) ||
			state.type !== edge.type
		);
	}

	/**
	 * Cache the current state of an edge.
	 */
	private cacheEdgeState(edge: UIEdge, sourceNode: UINode, targetNode: UINode): EdgeState {
		return {
			sourceX: sourceNode.position.x,
			sourceY: sourceNode.position.y,
			targetX: targetNode.position.x,
			targetY: targetNode.position.y,
			selected: edge.selected ?? false,
			hovered: edge.hovered ?? false,
			type: edge.type
		};
	}

	/**
	 * Render edges to the SVG layer.
	 *
	 * @param edges - Edges to render
	 * @param nodeMap - Map of node IDs to nodes (for endpoint calculation)
	 */
	renderEdges(edges: UIEdge[], nodeMap: Map<string, UINode>): void {
		const newEdgeIds = new Set(edges.map((e) => e.id));

		// Remove elements for edges that are no longer present
		for (const edgeId of this.renderedEdgeIds) {
			if (!newEdgeIds.has(edgeId)) {
				this.removeEdgeElement(edgeId);
			}
		}

		// Update or create elements for current edges
		for (const edge of edges) {
			this.renderEdge(edge, nodeMap);
		}

		this.renderedEdgeIds = newEdgeIds;
	}

	/**
	 * Render a single edge.
	 */
	private renderEdge(edge: UIEdge, nodeMap: Map<string, UINode>): void {
		const sourceNode = nodeMap.get(edge.source);
		const targetNode = nodeMap.get(edge.target);

		// Skip if either node doesn't exist
		if (!sourceNode || !targetNode) {
			this.removeEdgeElement(edge.id);
			return;
		}

		let path = this.edgeElements.get(edge.id);
		let isNewElement = false;

		if (!path) {
			path = this.createEdgeElement(edge);
			this.edgeElements.set(edge.id, path);
			this.edgeGroup.appendChild(path);
			isNewElement = true;
		}

		// Check if edge has changed
		const cachedState = this.edgeStates.get(edge.id);
		const edgeHasChanged = !cachedState || this.edgeChanged(edge, sourceNode, targetNode, cachedState);

		// Skip update if nothing changed
		if (!isNewElement && !edgeHasChanged) {
			return;
		}

		this.updateEdgeElement(path, edge, sourceNode, targetNode);

		// Cache the new state
		this.edgeStates.set(edge.id, this.cacheEdgeState(edge, sourceNode, targetNode));
	}

	/**
	 * Create a new SVG path element for an edge.
	 */
	private createEdgeElement(edge: UIEdge): SVGPathElement {
		const path = document.createElementNS(SVG_NS, 'path');
		path.classList.add(CSS.edge);
		path.dataset.edgeId = edge.id;
		path.dataset.type = edge.type;
		return path;
	}

	/**
	 * Update an edge element's path and state.
	 */
	private updateEdgeElement(
		path: SVGPathElement,
		edge: UIEdge,
		sourceNode: UINode,
		targetNode: UINode
	): void {
		// Calculate endpoints
		const source = getNodeCenter(sourceNode);
		const target = getNodeCenter(targetNode);

		// Store computed endpoints on edge
		edge.sourcePoint = source;
		edge.targetPoint = target;

		// Generate and set path data
		const pathData = generateEdgePath(source, target);
		path.setAttribute('d', pathData);

		// Update state classes
		path.classList.toggle(CSS.selected, edge.selected ?? false);
		path.classList.toggle(CSS.hovered, edge.hovered ?? false);

		// Update data attribute for type-specific styling
		path.dataset.type = edge.type;
	}

	/**
	 * Remove an edge element.
	 */
	private removeEdgeElement(edgeId: string): void {
		const path = this.edgeElements.get(edgeId);
		if (path) {
			path.remove();
			this.edgeElements.delete(edgeId);
		}
		// Clean up cached state
		this.edgeStates.delete(edgeId);
	}

	/**
	 * Get the SVG path element for an edge.
	 */
	getEdgeElement(edgeId: string): SVGPathElement | null {
		return this.edgeElements.get(edgeId) ?? null;
	}

	/**
	 * Clear all edges.
	 */
	clear(): void {
		for (const path of this.edgeElements.values()) {
			path.remove();
		}
		this.edgeElements.clear();
		this.edgeStates.clear();
		this.renderedEdgeIds.clear();
	}

	/**
	 * Clean up resources.
	 */
	destroy(): void {
		this.clear();
		this.svg.remove();
		this.container.classList.remove(CSS.layer);
	}
}
